import { useLayoutEffect } from 'react'
import type { ComponentType, SVGProps } from 'react'
import { useOutletContext } from 'react-router-dom'

import {
  AnalyticsIcon,
  CustomersIcon,
  DeliveryIcon,
  InventoryIcon,
  OrdersIcon,
  PaymentsIcon,
  ProductsIcon,
  PromotionsIcon,
  PointOfSaleIcon,
  QrCodeIcon,
  ReportsIcon,
  SettingsIcon,
  StaffIcon,
} from '@/components/ui/icons'
import { paths } from '@/routes/paths'

export interface PageIdentity {
  title: string
  subtitle: string
  icon: ComponentType<SVGProps<SVGSVGElement>>
}

/**
 * What the app bar says when it is not saying "NexQR".
 *
 * The logo marks home; everywhere else the bar names where you actually are.
 * Every module is registered here, which is what lets the modules themselves
 * carry no heading — one title per screen, in one place, with the way back
 * beside it.
 *
 * Keyed by exact pathname: `/staff/invitations/accept` is a different screen
 * from `/staff`, and a prefix match would label it as Staff.
 */
const PRODUCTS_IDENTITY: PageIdentity = {
  title: 'Products',
  subtitle: 'Your catalogue, categories, modifiers and options',
  icon: ProductsIcon,
}

const INVENTORY_IDENTITY: PageIdentity = {
  title: 'Inventory',
  subtitle: 'Stock, counts, recipes, modifiers, units and locations',
  icon: InventoryIcon,
}

const REPORTS_IDENTITY: PageIdentity = {
  title: 'Reports',
  subtitle: 'Sales, product mix, staff, cost and close-outs',
  icon: ReportsIcon,
}

const PAGE_IDENTITIES: Record<string, PageIdentity> = {
  [paths.overview]: {
    title: 'Dashboard',
    subtitle: 'Live trading health across your branches',
    icon: AnalyticsIcon,
  },
  [paths.orders]: {
    title: 'Orders',
    subtitle: 'Everything customers have sent you',
    icon: OrdersIcon,
  },
  /**
   * One identity for three paths.
   *
   * Categories and Modifiers were folded into Products as tabs, but their old
   * routes still resolve so bookmarks keep working — see `app-routes.tsx`. They
   * render the same screen, so the bar has to name the same screen: keyed
   * separately because the map is an exact pathname lookup, not a prefix one.
   */
  [paths.products]: PRODUCTS_IDENTITY,
  [paths.categories]: PRODUCTS_IDENTITY,
  [paths.modifiers]: PRODUCTS_IDENTITY,
  [paths.discounts]: {
    title: 'Promotions',
    subtitle: 'Discount campaigns, and when they run',
    icon: PromotionsIcon,
  },
  /**
   * One identity for the eight tab routes — they are one screen. A tab added to
   * `app-routes.tsx` needs its line here too, or its bar falls back to the logo.
   */
  [paths.inventory]: INVENTORY_IDENTITY,
  [paths.inventoryOverview]: INVENTORY_IDENTITY,
  [paths.inventoryAdjustments]: INVENTORY_IDENTITY,
  [paths.inventoryCounts]: INVENTORY_IDENTITY,
  [paths.inventoryRecipes]: INVENTORY_IDENTITY,
  [paths.inventoryModifiers]: INVENTORY_IDENTITY,
  [paths.inventoryUnits]: INVENTORY_IDENTITY,
  [paths.inventoryLocations]: INVENTORY_IDENTITY,
  [paths.tables]: {
    title: 'QR Ordering',
    subtitle: 'Tables, and the codes customers order from',
    icon: QrCodeIcon,
  },
  [paths.pos]: {
    title: 'Point of Sale',
    subtitle: 'Registers, floor and cash oversight',
    icon: PointOfSaleIcon,
  },
  [paths.storefront]: {
    title: 'Storefront',
    subtitle: 'Banners, and the pages customers read',
    icon: SettingsIcon,
  },
  [paths.business]: {
    title: 'Business',
    subtitle: 'Your details and the branches you trade from',
    icon: SettingsIcon,
  },
  [paths.operations]: {
    title: 'Operations',
    subtitle: 'Opening hours and delivery zones',
    icon: DeliveryIcon,
  },
  [paths.payments]: {
    title: 'Payment',
    subtitle: 'Transactions, settlements and collections',
    icon: PaymentsIcon,
  },
  [paths.customers]: {
    title: 'Customers',
    subtitle: 'Who buys from you, and what they have spent',
    icon: CustomersIcon,
  },
  [paths.reports]: REPORTS_IDENTITY,
  [paths.staff]: {
    title: 'Staff & Employees',
    subtitle: 'Merchant users, roles and POS employees',
    icon: StaffIcon,
  },
}

/**
 * Screens whose path carries an id, matched by prefix. Kept apart from the
 * exact map above so the prefix match cannot leak into it: only the paths
 * listed here are matched this way.
 */
const PREFIX_IDENTITIES: [prefix: string, identity: PageIdentity][] = [
  // A recipe's editor is part of Inventory, one level down from its Recipes tab.
  [`${paths.inventoryRecipes}/`, INVENTORY_IDENTITY],
]

export function pageIdentityFor(pathname: string): PageIdentity | null {
  return (
    PAGE_IDENTITIES[pathname] ??
    PREFIX_IDENTITIES.find(([prefix]) => pathname.startsWith(prefix))?.[1] ??
    null
  )
}

/**
 * What a screen deeper inside a module changes in the bar. Each field left out
 * keeps the module's own — a screen can keep the module's title and only move
 * the back arrow to where it was opened from.
 *
 * The icon always stays the module's.
 */
export interface PageIdentityOverride {
  title?: string
  subtitle?: string
  /** Where the back arrow goes. Home when omitted. */
  backTo?: string
}

/** What `AppLayout` hands its pages through the outlet. */
export interface ShellOutletContext {
  setPageIdentity: (identity: PageIdentityOverride | null) => void
}

/**
 * Names the current screen in the app bar, for as long as the caller is mounted.
 *
 * A layout effect rather than a plain one, so the bar never paints the module's
 * name for a frame before the screen's own replaces it. Null hands the bar back to
 * the module's own identity.
 */
export function usePageIdentity(identity: PageIdentityOverride | null) {
  const context = useOutletContext<ShellOutletContext | undefined>()
  const setPageIdentity = context?.setPageIdentity
  const isSet = identity !== null
  const title = identity?.title
  const subtitle = identity?.subtitle
  const backTo = identity?.backTo

  useLayoutEffect(() => {
    if (!setPageIdentity) return

    setPageIdentity(isSet ? { title, subtitle, backTo } : null)

    return () => setPageIdentity(null)
  }, [setPageIdentity, isSet, title, subtitle, backTo])
}
